import { cn } from "@/lib/utils";
import { useTranslation } from "@repo/shared/i18n";
import type { CreditFeatureId } from "@repo/shared/redux";
import {
  creditsVisibleSelector,
  featureCostSelector,
} from "@repo/shared/redux";
import { useSelector } from "react-redux";

import { CreditIcon } from "./CreditIcon";

type Props = {
  featureId: CreditFeatureId;
  /**
   * Whether this particular action would be charged. Metered features only
   * charge for a Qualifying call (see qualifying.ts) — solving from the market
   * costs nothing — so the marker follows the same predicate the gate does
   * rather than the feature alone.
   */
  when?: boolean;
  /**
   * `"beside-button"` hangs the price off the left edge of a `relative` parent,
   * out of flow. Centred buttons need this: a marker in flow would shift the
   * button sideways the moment a price appeared. Buttons already anchored to an
   * edge (a right-aligned `ml-auto` group) don't — they grow leftward into the
   * gap — so those stay in flow and pass nothing.
   */
  placement?: "in-flow" | "beside-button";
  /** How many of the action this prices, e.g. a batch of solves. */
  count?: number;
  className?: string;
};

/**
 * The out-of-flow offset, kept here rather than at each button: three surfaces
 * used to spell it out identically, so nudging one silently misaligned the
 * others. Callers supply the `relative` parent; this positions against it.
 */
const BESIDE_BUTTON = "absolute right-full top-1/2 -translate-y-1/2 mr-2";

/**
 * The price of an action: a coin and a number.
 *
 * Deliberately the same two glyphs, in the same arrangement, as the balance in
 * the top nav (CreditsNavChip) — if the running total and the per-action price
 * look alike, there is only one thing to learn.
 *
 * It carries no container of its own, which decides where it may be placed:
 * **beside or beneath an action, not inside a brand-blue one.** The coin is
 * built from the brand blue, so on a primary button it has almost no contrast —
 * the earlier in-button version was invisible on exactly the two buttons that
 * matter most. Off the button it sits on page ground, where it reads fine.
 *
 * The one in-button exception is the evolution chain wall (EvolutionChain),
 * which is a translucent grey surface filling its area edge to edge: the
 * contrast problem doesn't arise there, and there is no outside to hang off.
 *
 * The number is rendered rather than left to the `title`, because a tooltip is
 * hover-only and therefore absent on touch — where most of these actions are
 * taken.
 *
 * Renders nothing when credits are not to be spoken of yet (see
 * creditsVisibleSelector) or when the backend hasn't quoted a price; a marker
 * with no number behind it would be a guess.
 */
export const CreditCostMarker = ({
  featureId,
  when = true,
  placement = "in-flow",
  count = 1,
  className,
}: Props) => {
  const { t } = useTranslation("subscription");
  const visible = useSelector(creditsVisibleSelector);
  const cost = useSelector(featureCostSelector(featureId));

  if (!visible || !when || cost <= 0) return null;
  const total = cost * count;

  return (
    <span
      title={t("credits.costTitle", {
        cost: total,
        noun: t("credits.noun", { count: total }),
      })}
      className={cn(
        "inline-flex shrink-0 flex-row items-center gap-x-1.5 text-sm leading-none text-dark-secondary",
        placement === "beside-button" && BESIDE_BUTTON,
        className,
      )}
    >
      <CreditIcon />
      <span className="font-bold tabular-nums">{total}</span>
    </span>
  );
};
